import './styles.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Form, Radio, Row, Space } from 'antd'
import type { FormInstance } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import clsx from 'clsx'

import B2becSpinner from '@/components/B2becSpinner'
import Translator from '@/components/B2becTranslation'
import CustomButton from 'components/CustomButton'
import { B2becButtonBack } from 'components/CustomButton/B2becButtonIcon'
import { useAppSelector } from '@/store/hook'
import { getSelectedMachineData } from '@/store/selectors/serviceCaseSelector'
import { useDeviceDetect } from '@/hooks'
import useAsync from '@/hooks/useAsync'
import usePermission from '@/hooks/usePermission'
import { ASYNC_STATUS, SERVICE_ENTRY, SERVICE_TYPE } from '@/libs/constants'
import { MODULE_SERVICE_RIGHTS } from '@/libs/constants/modulerights'

import FailureDescriptionSection from '../../components/FailureDescriptionSection'
import UploadDamagedPicture from '../../components/UploadDamagedPicture'
import { apiGetProductServiceFAQs } from './SelectServiceHelper'

interface ServiceSelectStepProps {
  form: FormInstance
  currentTab: number
  handleChangeTab: (...args: unknown[]) => unknown
}

const ServiceSelectStep = ({
  currentTab,
  handleChangeTab,
  form,
}: ServiceSelectStepProps) => {
  const { t } = useTranslation()
  const { isMobile } = useDeviceDetect()
  const [isShowFaq, setShowFaq] = useState(true)
  const selectedMachineData = useAppSelector(getSelectedMachineData)

  const {
    verifiedPermissions: [canCreateRepairService, canCreateMaintenanceService],
  } = usePermission([
    MODULE_SERVICE_RIGHTS.CREATE_REPAIR_SERVICE_CASE,
    MODULE_SERVICE_RIGHTS.CREATE_MAINTENANCE_SERVICE_CASE,
  ])

  const {
    serviceEntry,
    id: machineId,
    name: machineName,
  } = selectedMachineData

  const {
    execute,
    value: faqList,
    status,
  } = useAsync(apiGetProductServiceFAQs, false)
  const selectedFailureDescriptions = Form.useWatch('failureDescriptions', form)

  useEffect(() => {
    if (machineId && machineName) {
      execute(machineId)
    }
  }, [execute, machineId, machineName])

  useEffect(() => {
    if (selectedFailureDescriptions?.length > 0) {
      form.validateFields(['failureDescriptionNotes']).catch(() => undefined)
    }
  }, [form, selectedFailureDescriptions])

  const onChangeServiceTypeHandler = useCallback((e) => {
    setShowFaq(e?.target?.value !== SERVICE_TYPE.MAINTENANCE)
  }, [])

  const serviceTypes = useMemo(() => {
    const availableServiceTypes = []
    if (canCreateRepairService) {
      availableServiceTypes.push({
        label: t('service.serviceType.repair'),
        value: SERVICE_TYPE.REPAIR,
      })
    }
    if (canCreateMaintenanceService) {
      availableServiceTypes.push({
        label: t('service.serviceType.maintenance'),
        value: SERVICE_TYPE.MAINTENANCE,
      })
    }
    return availableServiceTypes
  }, [t, canCreateMaintenanceService, canCreateRepairService])

  const renderDescriptionSection = () =>
    serviceEntry === SERVICE_ENTRY.MACHINE && (
      <div className="service-step__service__description__wrapper">
        <div className="service-step__service__description__title">
          {t('service.selectService.descriptionTitle')}
        </div>
        <div className="service-step__service__description__text">
          {t('service.selectService.descriptionText')}
        </div>
      </div>
    )

  const renderServiceSelectionArea = useCallback(
    () =>
      serviceEntry === SERVICE_ENTRY.MACHINE && (
        <div className="service-request__layout--shrink">
          <div className="service-step__service__select-desire-service-text">
            {t('service.selectService.selectDesiredService')}
          </div>
          <Form.Item
            className="custom-form-item"
            initialValue={serviceTypes[0].value}
            name="serviceType"
          >
            <Radio.Group onChange={onChangeServiceTypeHandler}>
              <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                {serviceTypes.map((serviceType) => (
                  <Radio key={serviceType?.value} value={serviceType?.value}>
                    <span className="font-weight-medium">
                      {serviceType?.label}
                    </span>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
        </div>
      ),
    [serviceEntry, t, serviceTypes, onChangeServiceTypeHandler, isMobile],
  )

  const renderFailureDescription = useCallback(() => {
    const isFurtherNotesRequired = !(selectedFailureDescriptions?.length > 0)

    return (
      isShowFaq && (
        <>
          {status === ASYNC_STATUS.PENDING
            ? <B2becSpinner isLoading={status === ASYNC_STATUS.PENDING} />
            : (
              <div className="service-step__service__FAQ__wrapper">
                {faqList && <FailureDescriptionSection FAQList={faqList} form={form} />}
              </div>
            )}

          {/* further notes section */}
          <Row>
            <Col lg={10} md={10} span={24}>
              <div className="service-step__service__FAQ__wrapper service-request__layout--shrink">
                <Form.Item
                  className="custom-form-item"
                  dependencies={['failureDescriptions']}
                  name="failureDescriptionNotes"
                  required={isFurtherNotesRequired}
                  label={
                    <div className="FAQ__failures__notes-title">
                      <Translator
                        isComponent
                        value="service.selectService.addFurtherNotes"
                        htmlTags={[
                          <span
                            key="further-notes-title"
                            className={clsx(
                              isFurtherNotesRequired
                                ? 'FAQ__failures__notes-title--required'
                                : 'FAQ__failures__notes-title--optional',
                            )}
                          />,
                        ]}
                      />
                    </div>
                  }
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const hasSelectedFailureDescriptions =
                          getFieldValue('failureDescriptions')?.length > 0

                        if (hasSelectedFailureDescriptions || value) {
                          return Promise.resolve()
                        }

                        return Promise.reject(
                          new Error(t('form.validate.furtherNotesRequired')),
                        )
                      },
                    }),
                  ]}
                >
                  <TextArea
                    placeholder={t(
                      'service.selectService.addFurtherNotesPlaceholder',
                    )}
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>
        </>
      )
    )
  }, [isShowFaq, status, faqList, form, selectedFailureDescriptions, t])

  return (
    <div className="service-step__service">
      {renderDescriptionSection()}

      {/* Select service type section */}
      {renderServiceSelectionArea()}

      {/* Select failure descrition section */}
      {renderFailureDescription()}

      {/* Add damaged pictures section */}
      <div className="service-step__service__upload-damaged-picture">
        <UploadDamagedPicture form={form} />
      </div>

      <div className="service-request__layout--shrink">
        <Divider />
      </div>

      <Row gutter={[24, 24]}>
        {serviceEntry === SERVICE_ENTRY.REPAIR && (
          <Col
            lg={8}
            md={{ span: 12, order: 1 }}
            sm={{ order: 2 }}
            span={24}
            xs={{ order: 2 }}
          >
            <div className="service-request__layout--shrink">
              <B2becButtonBack
                block
                type="ghost"
                onClick={() => {
                  handleChangeTab(+currentTab - 1)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              >
                {t('service.addressAndDate.button.backToSelectMachine')}
              </B2becButtonBack>
            </div>
          </Col>
        )}
        <Col
          lg={{ span: 8, push: 8 }}
          md={{ span: 12, order: 2 }}
          sm={{ order: 1 }}
          span={24}
          xs={{ order: 1 }}
        >
          <div className="service-request__layout--shrink">
            <CustomButton
              block
              onClick={() => {
                handleChangeTab(+currentTab + 1)
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
            >
              {t('service.button.next')}
            </CustomButton>
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default ServiceSelectStep
